import { z } from 'zod';
const domainRegex = /^(?:localhost|(?:\*\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,})$/i;
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex color, e.g. #4f46e5');
export const WebsiteStatus = z.enum(['active', 'paused', 'disabled']);
export const CreateWebsiteInput = z.object({
    name: z.string().min(1).max(100),
    domain: z
        .string()
        .min(1)
        .max(253)
        .toLowerCase()
        .regex(domainRegex, 'must be a valid hostname, optionally prefixed with *.'),
});
/**
 * Widget customization fields.
 *
 * - `brandName`, `accentColor`, `systemPromptOverride` are plan-gated; the
 *   server stores any value the user submits but the widget loader only
 *   surfaces them when the website's current plan permits them. This lets
 *   a downgrade preserve the user's settings if they ever upgrade again.
 * - `greeting` and `hiddenUrls` are available on every plan.
 * - `hidePoweredBy` requires a plan with `features.hidePoweredBy === true`.
 */
export const UpdateWebsiteInput = CreateWebsiteInput.partial().extend({
    status: WebsiteStatus.optional(),
    brandName: z.string().min(1).max(100).nullable().optional(),
    greeting: z.string().min(1).max(500).nullable().optional(),
    accentColor: hexColor.nullable().optional(),
    hidePoweredBy: z.boolean().optional(),
    hiddenUrls: z.array(z.string().min(1).max(500)).max(50).optional(),
    systemPromptOverride: z.string().min(1).max(5000).nullable().optional(),
    /**
     * Per-website master switch for the human-handoff feature. The API
     * always allows turning this OFF; turning ON requires the website's
     * effective plan to satisfy `maxAgents > 0`. The persisted value is
     * preserved even when the plan currently disallows it (mirrors the
     * forgiving pattern used for other plan-gated fields), so a future
     * upgrade re-activates the saved preference automatically.
     */
    handoffEnabled: z.boolean().optional(),
});
export const WebsiteDTO = z.object({
    id: z.string().uuid(),
    ownerId: z.string().uuid(),
    name: z.string(),
    domain: z.string(),
    embedKey: z.string().uuid(),
    status: WebsiteStatus,
    brandName: z.string().nullable(),
    greeting: z.string().nullable(),
    accentColor: z.string().nullable(),
    hidePoweredBy: z.boolean(),
    hiddenUrls: z.array(z.string()),
    systemPromptOverride: z.string().nullable(),
    /** Persisted preference. May be `true` even when the plan disallows
     *  handoff today (in which case `handoffAvailable === false`). */
    handoffEnabled: z.boolean(),
    /** Effective state: `handoffEnabled && plan.maxAgents > 0`. The widget
     *  loader and orchestrator gate on this, NOT on `handoffEnabled` alone. */
    handoffAvailable: z.boolean(),
    createdAt: z.string(),
});
export const WebsiteWithSnippet = WebsiteDTO.extend({
    snippet: z.string(),
    widgetUrl: z.string(),
});
//# sourceMappingURL=website.js.map